import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useWeather } from '../../context';

export function TropicalCycloneLayer({ map }: { map: mapboxgl.Map }) {
  const { weatherData } = useWeather();
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  useEffect(() => {
    if (!map || !weatherData?.length) return;

    const cleanup = () => {
      if (map.getStyle()) {
        if (map.getLayer('cyclone-tracks')) map.removeLayer('cyclone-tracks');
        if (map.getLayer('cyclone-points')) map.removeLayer('cyclone-points');
        if (map.getSource('cyclone-data')) map.removeSource('cyclone-data');
      }
      if (popupRef.current) popupRef.current.remove();
    };

    cleanup();

    try {
      // Process cyclone data
      const features = weatherData.map(cyclone => {
        // Extract track points from the response
        const trackPoints = cyclone.track.map((point: any) => ({
          coordinates: point.location.coordinates,
          details: point.details,
          timestamp: point.timestamp,
          dateTimeISO: point.dateTimeISO
        }));

        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: trackPoints.map(p => p.coordinates)
          },
          properties: {
            id: cyclone.id,
            name: cyclone.profile?.name || 'Unnamed Storm',
            maxStormType: cyclone.profile?.maxStormType || 'Unknown',
            maxStormCat: cyclone.profile?.maxStormCat || 'Unknown',
            maxWindSpeed: cyclone.profile?.windSpeed?.maxMPH || 0,
            minPressure: cyclone.profile?.pressure?.minMB || 0,
            startTime: cyclone.profile?.lifespan?.startDateTimeISO,
            endTime: cyclone.profile?.lifespan?.endDateTimeISO
          }
        };
      });

      // Create point features for each track position
      const pointFeatures = weatherData.flatMap(cyclone =>
        cyclone.track.map((point: any) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: point.location.coordinates
          },
          properties: {
            id: cyclone.id,
            name: cyclone.profile?.name || 'Unnamed Storm',
            stormType: point.details?.stormType || 'Unknown',
            stormCat: point.details?.stormCat || 'Unknown',
            windSpeed: point.details?.windSpeedMPH || 0,
            pressure: point.details?.pressureMB || 0,
            time: point.dateTimeISO,
            movement: point.details?.movement
          }
        }))
      );

      // Add source
      map.addSource('cyclone-data', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [...features, ...pointFeatures]
        }
      });

      // Add track lines
      map.addLayer({
        id: 'cyclone-tracks',
        type: 'line',
        source: 'cyclone-data',
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-color': [
            'match',
            ['get', 'maxStormCat'],
            'TD', '#00ff00',
            'TS', '#ffff00',
            'H1', '#ff9900',
            'H2', '#ff6600',
            'H3', '#ff3300',
            'H4', '#ff0000',
            'H5', '#990000',
            '#ffffff'
          ],
          'line-width': 2,
          'line-opacity': 0.8
        }
      });

      // Add points
      map.addLayer({
        id: 'cyclone-points',
        type: 'circle',
        source: 'cyclone-data',
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': 6,
          'circle-color': [
            'match',
            ['get', 'stormCat'],
            'TD', '#00ff00',
            'TS', '#ffff00',
            'H1', '#ff9900',
            'H2', '#ff6600',
            'H3', '#ff3300',
            'H4', '#ff0000',
            'H5', '#990000',
            '#ffffff'
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff'
        }
      });

      // Create popup
      popupRef.current = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'weather-data-popup-container'
      });

      // Add click handlers
      const handleClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
        if (!e.features?.length) return;

        const feature = e.features[0];
        const props = feature.properties;
        if (!props) return;

        const html = `
          <div class="p-4">
            <h4 class="text-lg font-bold mb-2">${props.name}</h4>
            <div class="space-y-2 text-sm">
              <p><span class="text-[var(--secondary-text)]">Storm Type:</span> ${props.stormType}</p>
              <p><span class="text-[var(--secondary-text)]">Category:</span> ${props.stormCat}</p>
              <p><span class="text-[var(--secondary-text)]">Wind Speed:</span> ${props.windSpeed} mph</p>
              <p><span class="text-[var(--secondary-text)]">Pressure:</span> ${props.pressure} mb</p>
              <p><span class="text-[var(--secondary-text)]">Time:</span> ${new Date(props.time).toLocaleString()}</p>
              ${props.movement ? `
                <p><span class="text-[var(--secondary-text)]">Movement:</span> ${props.movement.direction} at ${props.movement.speedMPH} mph</p>
              ` : ''}
            </div>
          </div>
        `;

        popupRef.current!
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(map);
      };

      map.on('click', 'cyclone-points', handleClick);
      map.on('mouseenter', 'cyclone-points', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'cyclone-points', () => {
        map.getCanvas().style.cursor = '';
      });

      return cleanup;
    } catch (error) {
      console.error('Error rendering tropical cyclone layer:', error);
      cleanup();
    }
  }, [map, weatherData]);

  return null;
}